import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import KYCCard from '../components/KYCCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Briefcase, FileText, TrendingUp, Plus, Share2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreelancerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [gigs, setGigs] = useState([]);
  const [bids, setBids] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGigDialog, setShowGigDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newGig, setNewGig] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    delivery_days: '',
    tags: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gigsRes, bidsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/gigs/my`),
        axios.get(`${API}/bids/my`),
        axios.get(`${API}/contracts`),
      ]);
      setGigs(gigsRes.data);
      setBids(bidsRes.data);
      setContracts(contractsRes.data.filter(c => c.freelancer_id === user.id));
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGig = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gigs`, {
        ...newGig,
        price: parseFloat(newGig.price),
        delivery_days: parseInt(newGig.delivery_days),
        tags: newGig.tags.split(',').map(t => t.trim()),
      });
      toast.success('Gig created successfully!');
      setShowGigDialog(false);
      setNewGig({ title: '', description: '', category: '', price: '', delivery_days: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create gig');
    }
  };

  const profileUrl = user?.user_code
    ? `${window.location.origin}/u/${user.user_code}`
    : '';

  const handleShareProfile = () => {
    if (!profileUrl) {
      toast.error('Profile link not available');
      return;
    }
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    toast.success('Profile link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const totalEarnings = contracts
    .filter(c => c.payment_status === 'released')
    .reduce((sum, c) => sum + c.amount, 0);

  const activeContracts = contracts.filter(c => c.status === 'active').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary" data-testid="dashboard-title">
              Freelancer Dashboard
            </h1>
            {profileUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleShareProfile}
                data-testid="share-profile-button"
                className="gap-2"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
                {copied ? 'Link Copied!' : 'Share Profile'}
              </Button>
            )}
          </div>
          <p className="text-muted-foreground" data-testid="dashboard-subtitle">
            Manage your gigs, bids, and contracts
          </p>
          {profileUrl && (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="profile-link-display">
              <span className="bg-slate-100 px-2 py-1 rounded font-mono truncate max-w-sm">{profileUrl}</span>
              <button
                onClick={handleShareProfile}
                className="text-primary hover:underline flex items-center gap-1"
                data-testid="copy-profile-link"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          )}
        </div>

        {/* KYC Verification */}
        <KYCCard onChange={fetchData} />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-earnings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <DollarSign className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">₹{totalEarnings.toLocaleString('en-IN')}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-active-contracts">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
              <Briefcase className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{activeContracts}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-gigs">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Gigs</CardTitle>
              <FileText className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{gigs.length}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-bids">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Bids</CardTitle>
              <TrendingUp className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-heading font-bold text-primary">{bids.filter(b => b.status === 'pending').length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Gigs Section */}
        <Card className="mb-8" data-testid="gigs-section">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl font-heading">My Gigs</CardTitle>
                <CardDescription>Manage your service offerings</CardDescription>
              </div>
              <Dialog open={showGigDialog} onOpenChange={setShowGigDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-secondary text-secondary-foreground" data-testid="create-gig-button">
                    <Plus className="w-4 h-4 mr-2" /> Create Gig
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create New Gig</DialogTitle>
                    <DialogDescription>List a service you offer to clients</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateGig} className="space-y-4" data-testid="create-gig-form">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={newGig.title}
                        onChange={(e) => setNewGig({ ...newGig, title: e.target.value })}
                        required
                        data-testid="gig-title-input"
                        placeholder="I will design a professional logo"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newGig.description}
                        onChange={(e) => setNewGig({ ...newGig, description: e.target.value })}
                        required
                        rows={4}
                        data-testid="gig-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Input
                          value={newGig.category}
                          onChange={(e) => setNewGig({ ...newGig, category: e.target.value })}
                          required
                          data-testid="gig-category-input"
                          placeholder="Design"
                        />
                      </div>
                      <div>
                        <Label>Price (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newGig.price}
                          onChange={(e) => setNewGig({ ...newGig, price: e.target.value })}
                          required
                          data-testid="gig-price-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Delivery Days</Label>
                        <Input
                          type="number"
                          value={newGig.delivery_days}
                          onChange={(e) => setNewGig({ ...newGig, delivery_days: e.target.value })}
                          required
                          data-testid="gig-delivery-input"
                        />
                      </div>
                      <div>
                        <Label>Tags (comma separated)</Label>
                        <Input
                          value={newGig.tags}
                          onChange={(e) => setNewGig({ ...newGig, tags: e.target.value })}
                          data-testid="gig-tags-input"
                          placeholder="logo, branding, design"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" data-testid="gig-submit-button">
                      Create Gig
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {gigs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8" data-testid="no-gigs-message">
                No gigs yet. Create your first gig to start earning!
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gigs.map((gig) => (
                  <div
                    key={gig.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/gigs/${gig.id}`)}
                    data-testid={`gig-${gig.id}`}
                  >
                    <h3 className="font-heading font-semibold text-lg mb-2">{gig.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{gig.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-heading font-bold text-secondary">₹{gig.price.toLocaleString('en-IN')}</span>
                      <Badge
                        variant="secondary"
                        className={
                          gig.status === 'pending_approval' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                          gig.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' :
                          gig.status === 'paused' ? 'bg-slate-100 text-slate-600' :
                          gig.status === 'deleted' ? 'bg-red-100 text-red-700 border-red-300' : ''
                        }
                        data-testid={`gig-status-${gig.id}`}
                      >
                        {gig.status === 'pending_approval' ? 'Pending Approval' :
                         gig.status === 'active' ? 'Live' :
                         gig.status === 'paused' ? 'Paused' :
                         gig.status === 'deleted' ? 'Rejected' : gig.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contracts Section */}
        <Card data-testid="contracts-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Recent Contracts</CardTitle>
            <CardDescription>Track your ongoing and completed work</CardDescription>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8" data-testid="no-contracts-message">
                No contracts yet. Start bidding on projects or wait for gig orders!
              </p>
            ) : (
              <div className="space-y-4">
                {contracts.slice(0, 5).map((contract) => (
                  <div
                    key={contract.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    data-testid={`contract-${contract.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-heading font-semibold">{contract.client_name}</h3>
                        <p className="text-sm text-muted-foreground">{contract.type === 'gig' ? 'Gig Order' : 'Project'}</p>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-bold text-secondary">₹{contract.amount.toLocaleString('en-IN')}</div>
                        <Badge variant={contract.status === 'active' ? 'default' : 'secondary'}>
                          {contract.status}
                        </Badge>
                      </div>
                    </div>
                    <Badge variant="outline" className="mt-2">
                      {contract.payment_status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FreelancerDashboard;
